// Pure helpers for claiming a gateway slug from the browser publisher (szzt-spec.md §11.1). The
// gateway governs a `slug → owner` index and serves `<slug>.<apex>/`; a publisher claims a slug for one
// of its OWN sites by POSTing a service-auth-JWT-authenticated request to the gateway's allocate-slug
// endpoint (served same-origin on the apex — see apps/gateway src/slug-alloc wiring). These are the
// PURE, unit-tested pieces; the network glue (mint the JWT via `com.atproto.server.getServiceAuth`, POST)
// lives in main.ts, like the rest of the DOM/OAuth glue.

// The gateway's slug endpoint paths (§11.1). MUST match @szzt/server src/slug-alloc.ts SLUG_ALLOC_PATH
// / SLUG_RELEASE_PATH — the two packages can't share the constant without a dependency, so the string is
// mirrored on both sides. Only allocate is wired into the UI; release (permanent retirement) is left out.
export const SLUG_ALLOC_PATH = "/.well-known/szzt/allocate-slug";

// The lexicon-method binding for the minted service-auth token. The gateway verifier ENFORCES `lxm`
// (H17): a token minted with any other value is rejected (401), so this MUST match @szzt/server
// src/slug-alloc.ts SLUG_ALLOC_LXM exactly (mirrored, not imported — see the PATH comment above).
export const SLUG_ALLOC_LXM = "app.szzt.allocateSlug";

/**
 * The gateway service DID a publisher names as the `aud` of its service-auth JWT (§10.1): `did:web:<apex>`.
 * The gateway only STRING-COMPARES this against its configured `SERVICE_DID` env — it never resolves the
 * DID — so `did:web:<apex>` needs no served did.json. Derived from the page host (the publisher is served
 * same-origin on the apex it publishes to), so it always names the gateway serving this page.
 */
export function serviceDidFor(hostname: string): string {
  return `did:web:${hostname}`;
}

/** The public URL a slug serves at: `https://<slug>.<apex>/` (§11 subdomain form). */
export function slugSiteUrl(slug: string, apex: string): string {
  return `https://${slug}.${apex}/`;
}

/** Normalize raw slug input the way the allocator compares it: trimmed + lowercased. */
export function normalizeSlug(raw: string): string {
  return raw.trim().toLowerCase();
}

// Client-side pre-check MIRRORING @szzt/moderation validateSlug's charset/length/cid-shape rules
// (packages/moderation src/slugs.ts). Advisory ONLY — the gateway allocator is authoritative and ALSO
// enforces reserved names, confusable-skeleton collision, permanent retirement, and DID blocklisting,
// none of which the browser can check. Kept to charset/length/cid-shape (small, stable regexes) so it
// gives instant feedback without duplicating the drift-prone reserved list.
const SLUG_CHARSET = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
const CID_LABEL = /^baf[yk][a-z2-7]{20,}$/;
const MIN_LENGTH = 3;
const MAX_LENGTH = 63;

/**
 * A human-readable reason `slug` (already normalized) can't be a valid slug, or undefined if it passes
 * the client-side checks. Undefined does NOT guarantee the gateway will allocate it — the slug may still
 * be refused as reserved / taken / confusable / retired, which surfaces via {@link slugOutcomeError}.
 */
export function slugInputError(slug: string): string | undefined {
  if (slug === "") return "Enter a short name.";
  if (slug.length < MIN_LENGTH) return `Too short (at least ${MIN_LENGTH} characters).`;
  if (slug.length > MAX_LENGTH) return `Too long (at most ${MAX_LENGTH} characters).`;
  if (!SLUG_CHARSET.test(slug) || slug.includes("--")) {
    return "Use lowercase letters, digits, and single hyphens (not at the ends).";
  }
  if (CID_LABEL.test(slug)) return "That name is reserved for content addresses.";
  return undefined;
}

/**
 * A friendly message for a FAILED slug-allocation response body. The gateway returns a
 * @szzt/server `SlugOutcome` as JSON (`{ kind, reason? }`), or `{ error }` on a malformed request.
 * Defensive — the body is untrusted network JSON, so a non-conforming shape falls back to a generic line.
 */
export function slugOutcomeError(body: unknown): string {
  if (body === null || typeof body !== "object") return "Could not claim that name.";
  const kind = (body as { kind?: unknown }).kind;
  const reason = (body as { reason?: unknown }).reason;
  const error = (body as { error?: unknown }).error;
  if (typeof reason === "string") {
    switch (reason) {
      case "taken":
        return "That name is already taken.";
      case "confusable":
        return "That name is too similar to an existing one.";
      case "retired":
        return "That name was released before and can't be reused.";
      case "reserved":
        return "That name is reserved.";
      case "charset":
        return "Use lowercase letters, digits, and single hyphens (not at the ends).";
      case "length":
        return `Use ${MIN_LENGTH}–${MAX_LENGTH} characters.`;
      case "cid-shape":
        return "That name is reserved for content addresses.";
      case "did-blocked":
        return "This account isn't allowed to claim names.";
      default:
        return `Could not claim that name (${reason}).`;
    }
  }
  if (typeof error === "string") return error;
  if (kind === "unauthorized") return "Not authorized — try signing in again.";
  if (kind === "rate-limited") return "Too many attempts — wait a moment and try again.";
  if (kind === "not-found") return "Name not found.";
  return "Could not claim that name.";
}
